/** @typedef {"metric" | "imperial"} UnitSystem */

export const MM_PER_INCH = 25.4;

const FRACTION_DENOMINATORS = [2, 4, 8, 16, 32];

/**
 * @param {number} mm
 * @returns {number}
 */
export function mmToInches(mm) {
  return mm / MM_PER_INCH;
}

/**
 * @param {number} inches
 * @returns {number}
 */
export function inchesToMm(inches) {
  return inches * MM_PER_INCH;
}

/**
 * @param {number} value
 * @param {number} denominator
 * @returns {{ whole: number, numerator: number, denominator: number }}
 */
export function decimalInchesToFraction(value, denominator = 16) {
  const sign = value < 0 ? -1 : 1;
  const abs = Math.abs(value);
  const whole = Math.floor(abs);
  const fractional = abs - whole;
  let numerator = Math.round(fractional * denominator);
  let denom = denominator;

  if (numerator === denom) {
    return { whole: sign * (whole + 1), numerator: 0, denominator: denom };
  }

  while (numerator > 0 && numerator % 2 === 0 && denom % 2 === 0) {
    numerator /= 2;
    denom /= 2;
  }

  return { whole: sign * whole, numerator, denominator: denom };
}

/**
 * @param {{ whole?: number, numerator?: number, denominator?: number }} parts
 * @returns {number}
 */
export function fractionPartsToDecimal({ whole = 0, numerator = 0, denominator = 1 }) {
  const sign = whole < 0 ? -1 : 1;
  const absWhole = Math.abs(whole);
  const frac = denominator ? numerator / denominator : 0;
  return sign * (absWhole + frac);
}

/**
 * @param {number} inches
 * @param {{ maxDenominator?: number, useFeet?: boolean }} [options]
 * @returns {string}
 */
export function formatInches(inches, options = {}) {
  const { maxDenominator = 16, useFeet = true } = options;
  const sign = inches < 0 ? "-" : "";
  const abs = Math.abs(inches);
  const useFeetFormat = useFeet && abs >= 48;

  if (useFeetFormat) {
    const feet = Math.floor(abs / 12);
    const remainder = abs - feet * 12;
    const { whole, numerator, denominator } = decimalInchesToFraction(remainder, maxDenominator);
    const inchPart = formatInchComponents(whole, numerator, denominator);
    if (inchPart === "0") {
      return `${sign}${feet}'`;
    }
    return `${sign}${feet}' ${inchPart}"`.trim();
  }

  const { whole, numerator, denominator } = decimalInchesToFraction(abs, maxDenominator);
  return `${sign}${formatInchComponents(whole, numerator, denominator)}"`;
}

/**
 * @param {number} whole
 * @param {number} numerator
 * @param {number} denominator
 * @returns {string}
 */
function formatInchComponents(whole, numerator, denominator) {
  const parts = [];
  if (whole !== 0) {
    parts.push(String(whole));
  }
  if (numerator > 0 && denominator > 0) {
    parts.push(`${numerator}/${denominator}`);
  }
  if (parts.length === 0) {
    return "0";
  }
  return parts.join(" ");
}

/**
 * @param {number} mm
 * @param {{ system?: UnitSystem, maxDenominator?: number, useFeet?: boolean, metricSuffix?: string }} [options]
 * @returns {string}
 */
export function formatLengthMm(mm, options = {}) {
  const {
    system = "metric",
    maxDenominator = 16,
    useFeet = true,
    metricSuffix = "mm",
  } = options;

  if (!Number.isFinite(mm)) {
    return "";
  }

  if (system === "imperial") {
    return formatInches(mmToInches(mm), { maxDenominator, useFeet });
  }

  const rounded = Math.round(mm * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : String(rounded);
  return `${text} ${metricSuffix}`.trim();
}

/**
 * @param {number} lengthMm
 * @param {number} widthMm
 * @param {{ system?: import("./length.js").UnitSystem, maxDenominator?: number, useFeet?: boolean, metricSuffix?: string }} [options]
 * @returns {string}
 */
export function formatSheetSize(lengthMm, widthMm, options = {}) {
  return `${formatLengthMm(lengthMm, options)} × ${formatLengthMm(widthMm, options)}`;
}

const SQ_MM_PER_SQ_IN = MM_PER_INCH * MM_PER_INCH;

/**
 * @param {number} mm2
 * @param {{ system?: UnitSystem }} [options]
 * @returns {string}
 */
export function formatAreaMm2(mm2, options = {}) {
  const { system = "metric" } = options;
  if (!Number.isFinite(mm2)) {
    return "";
  }

  if (system === "imperial") {
    const sqFt = mm2 / (SQ_MM_PER_SQ_IN * 144);
    if (sqFt >= 0.1) {
      const rounded = Math.round(sqFt * 100) / 100;
      const text = Number.isInteger(rounded) ? String(rounded) : String(rounded);
      return `${text} ft²`;
    }
    const sqIn = mm2 / SQ_MM_PER_SQ_IN;
    const rounded = Math.round(sqIn);
    return `${rounded} in²`;
  }

  const m2 = Math.round((mm2 / 1_000_000) * 100) / 100;
  const text = Number.isInteger(m2) ? String(m2) : String(m2);
  return `${text} m²`;
}

/**
 * Parse a single length token to mm (metric number or imperial fraction/decimal/feet).
 * @param {string} token
 * @param {{ defaultSystem?: UnitSystem }} [options]
 * @returns {number | null}
 */
export function parseLengthToken(token, options = {}) {
  const { defaultSystem = "metric" } = options;
  const raw = String(token || "").trim();
  if (!raw) {
    return null;
  }

  const normalized = raw
    .replace(/″|"/g, "")
    .replace(/′|'/g, "'")
    .trim();

  const feetInches = normalized.match(/^(-?\d+)\s*'\s*(.*)$/);
  if (feetInches) {
    const feet = Number(feetInches[1]);
    const inchPart = feetInches[2].trim();
    const inches = inchPart ? parseInchPart(inchPart) : 0;
    if (inches === null) {
      return null;
    }
    return inchesToMm(feet * 12 + inches);
  }

  if (/[\d./]/.test(normalized) && (normalized.includes("/") || normalized.includes(".") || defaultSystem === "imperial")) {
    const inches = parseInchPart(normalized);
    if (inches !== null) {
      return inchesToMm(inches);
    }
  }

  const metric = Number(normalized.replace(",", "."));
  if (Number.isFinite(metric)) {
    return defaultSystem === "imperial" ? inchesToMm(metric) : metric;
  }

  return null;
}

/**
 * @param {string} part
 * @returns {number | null} inches
 */
function parseInchPart(part) {
  const trimmed = part.trim();
  if (!trimmed) {
    return 0;
  }

  const mixed = trimmed.match(/^(-?\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (mixed) {
    return fractionPartsToDecimal({
      whole: Number(mixed[1]),
      numerator: Number(mixed[2]),
      denominator: Number(mixed[3]),
    });
  }

  const fraction = trimmed.match(/^(-?\d+)\s*\/\s*(\d+)$/);
  if (fraction) {
    return Number(fraction[1]) / Number(fraction[2]);
  }

  const decimal = Number(trimmed.replace(",", "."));
  if (Number.isFinite(decimal)) {
    return decimal;
  }

  return null;
}

export { FRACTION_DENOMINATORS };
